import React, { useEffect, useState } from 'react'
import { Select2 } from '@blueprintjs/select'
import { MenuItem, Position, Checkbox, Menu } from '@blueprintjs/core'
import { Button } from '@ui'
import { IUIButtonProps } from '@ui/buttons/Button'
import api, { ICostCenter } from '@api'
import fuzzy from 'fuzzaldrin-plus'
import { get, debounce } from 'lodash'
import { Flex, HighlightedText } from '@ui'

interface CostCenterSelectProps {
  buttonProps?: IUIButtonProps
  value: ICostCenter | null
  btnText?: string
  onChange: (item: ICostCenter) => void
  itemDisabled?: (item: ICostCenter) => boolean
  multi?: boolean
}

const CostCenterSelect = Select2<ICostCenter>

export default function CostCenterSelectWrapper({
  buttonProps,
  value,
  btnText,
  onChange,
  itemDisabled,
  multi,
}: CostCenterSelectProps) {
  const costCenters: ICostCenter[] = useCostCenters()
  const [query, setQuery] = React.useState('')
  const [items, setItems] = React.useState(costCenters.slice(0, 10))

  React.useEffect(() => {
    setItems(costCenters.slice(0, 10))
  }, [costCenters])

  const search = debounce(
    (query) =>
      setItems(
        query
          ? fuzzy.filter(costCenters, query, { key: 'name' }).slice(0, 10)
          : costCenters.slice(0, 10)
      ),
    0
  )

  return (
    <CostCenterSelect
      query={query}
      resetOnSelect
      popoverProps={{ minimal: true, position: Position.BOTTOM }}
      items={items}
      onQueryChange={(newQuery) => {
        setQuery(newQuery)
        search(newQuery)
      }}
      itemListRenderer={({ items, renderItem }) => {
        return <Menu style={{ minWidth: 300 }}>{items.map((item, i) => renderItem(item, i))}</Menu>
      }}
      itemRenderer={(costCenter, { handleClick, modifiers: { active }, query }) => (
        <MenuItem
          key={`cost-center-${costCenter.id}`}
          active={active}
          shouldDismissPopover={false}
          text={
            <Flex align="center">
              {multi && <Checkbox checked={itemDisabled && itemDisabled(costCenter)} />}
              <HighlightedText text={costCenter.name} query={query} />
            </Flex>
          }
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            if (multi) {
              e.preventDefault()
              onChange(costCenter)
              return
            }
            handleClick(e)
          }}
        />
      )}
      onItemSelect={onChange}
      noResults={<MenuItem disabled text="No results" />}
    >
      <Button
        {...buttonProps}
        text={value ? value.name : btnText || 'Select'}
        rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
      />
    </CostCenterSelect>
  )
}

function useCostCenters(): ICostCenter[] {
  const [costCenters, setCostCenters] = useState<ICostCenter[]>([])

  useEffect(() => {
    api.company.costCenters().then((costCenters) => {
      setCostCenters(costCenters)
    })
  }, [])
  return costCenters
}
